import React from "react";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  IconButton,
  InputBase,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Popover,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import labelService from "../services/labelService";
import { CreateLabelDialog } from "./CreateLabelDialog";
import task from "../services/task";


function findLabelsWithEmail(labels, emailID, parentPath = "") {
  let result = [];

  for (const label of labels) {
    const currentPath = parentPath ? `${parentPath}/${label.name}` : label.name;


    if (Array.isArray(label.emailId) && label.emailId.includes(emailID)) {
      result.push(currentPath);
    }


    if (label.children && label.children.length > 0) {
      result = result.concat(findLabelsWithEmail(label.children, emailID, currentPath));
    }
  }

  return result;
}

function findLabelsWithSubEmail(labels, subEmailID, parentPath = "") {
  let result = [];

  for (const label of labels) {
    const currentPath = parentPath ? `${parentPath}/${label.name}` : label.name;

    if (Array.isArray(label.subEmailIds) && label.subEmailIds.includes(subEmailID)) {
      result.push(currentPath);
    }

    if (label.children && label.children.length > 0) {
      result = result.concat(findLabelsWithSubEmail(label.children, subEmailID, currentPath));
    }
  }

  return result;
}

export function LabelList({ anchorEl, setAnchorEl, selectedEmailID, selectedSubEmailID, TaskId,isAdd=false,isSend,labelname,setLabelname,setLabelIds }) {
  const isSubEmailMode = Boolean(selectedSubEmailID);


  const open = Boolean(anchorEl);
  const [query, setQuery] = React.useState("");
  const [selected, setSelected] = React.useState([]);
  const [initialSelected, setInitialSelected] = React.useState([]);
  const [labels, setLabels] = React.useState([]);
  const [labelObjects, setLabelObjects] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [applying, setApplying] = React.useState(false);
  const [createDialogOpen, setCreateDialogOpen] = React.useState(false);

  React.useEffect(() => {
    if (open) fetchLabels();
  }, [open]);
  React.useEffect(() => {
    if ( selected.length >= 0 && labelname) {
      setLabelname(selected)
    }
    if (setLabelIds) {
      const findLabel = (labels, path, parentPath = "") => {
        for (const label of labels) {
          const currentPath = parentPath ? `${parentPath}/${label.name}` : label.name;
          if (currentPath === path) return label;
          if (label.children) {
            const found = findLabel(label.children, path, currentPath);
            if (found) return found;
          }
        }
        return null;
      };
      const ids = selected
        .map((path) => findLabel(labelObjects, path)?.id)
        .filter(Boolean);
      setLabelIds(ids);
    }
  }, [selected, labelObjects]);
  const flattenLabels = (labels, parentPath = "") => {
    let result = [];
    for (const label of labels) {
      const path = parentPath ? `${parentPath}/${label.name}` : label.name;
      result.push(path);
      if (label.children && label.children.length > 0) {
        result = result.concat(flattenLabels(label.children, path));
      }
    }
    return result;
  };

  async function fetchLabels() {
    setLoading(true);
    try {
      const res = await labelService.GET_Label_ALL();
      const fetchedLabels = res.data?.labels || [];
      setLabelObjects(fetchedLabels);


      const flattened = flattenLabels(fetchedLabels);
      setLabels(flattened);


      const preSelected = isAdd
        ? (Array.isArray(labelname) ? labelname : [])
        : isSubEmailMode
          ? findLabelsWithSubEmail(fetchedLabels, selectedSubEmailID)
          : findLabelsWithEmail(fetchedLabels, selectedEmailID);
      setSelected(preSelected);
      setInitialSelected(preSelected);
    } catch (error) {
      console.error("Error fetching labels:", error);
    } finally {
      setLoading(false);
    }
  }




  const handleClose = () => setAnchorEl(null);

  const handleOpenCreateDialog = () => {
    handleClose();
    setCreateDialogOpen(true);
  };

  const toggle = (label) => {
    setSelected((prev) =>
      prev.includes(label) ? prev.filter((v) => v !== label) : [...prev, label]
    );
  };

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return labels;
    return labels.filter((l) => l.toLowerCase().includes(q));
  }, [labels, query]);

  const hasChanges = React.useMemo(() => {
    if (selected.length !== initialSelected.length) return true;
    const a = [...selected].sort();
    const b = [...initialSelected].sort();
    return a.some((v, i) => v !== b[i]);
  }, [selected, initialSelected]);

  function findLabelByPath(labels, path, parentPath = "") {
    for (const label of labels) {
      const currentPath = parentPath
        ? `${parentPath}/${label.name}`
        : label.name;
      if (currentPath === path) return label;
      if (label.children) {
        const found = findLabelByPath(label.children, path, currentPath);
        if (found) return found;
      }
    }
    return null;
  }
  async function handleApply() {
    const toAdd = selected.filter((l) => !initialSelected.includes(l));
    const toRemove = initialSelected.filter((l) => !selected.includes(l));

    if (toAdd.length === 0 && toRemove.length === 0) return;
    setApplying(true);

    try {

      for (const labelPath of toAdd) {
        const labelObj = findLabelByPath(labelObjects, labelPath);
        if (!labelObj) continue;

        const res = await labelService.GET_Label_By_Id(labelObj.id);
        const fullLabel = res.data.data;
        if (!fullLabel) continue;

        const existingEmailIds = Array.isArray(fullLabel.emailId) ? fullLabel.emailId : [];
        const existingSubEmailIds = Array.isArray(fullLabel.subEmailIds) ? fullLabel.subEmailIds : [];

        const payload = {
          name: labelPath.split("/").pop(),
          color: "#FF0000",
        };

        if (isSubEmailMode) {
          payload.emailId = existingEmailIds;
          payload.subEmailIds = [...new Set([...existingSubEmailIds, selectedSubEmailID])];
        } else {
          payload.emailId = [...new Set([...existingEmailIds, selectedEmailID])];
          payload.subEmailIds = existingSubEmailIds;
        }

        await labelService.Update_Label_By_Id(labelObj.id, payload);
      }

      for (const labelPath of toRemove) {
        const labelObj = findLabelByPath(labelObjects, labelPath);
        if (!labelObj) continue;

        const res = await labelService.GET_Label_By_Id(labelObj.id);
        const fullLabel = res.data.data;
        if (!fullLabel) continue;

        const existingEmailIds = Array.isArray(fullLabel.emailId) ? fullLabel.emailId : [];
        const existingSubEmailIds = Array.isArray(fullLabel.subEmailIds) ? fullLabel.subEmailIds : [];

        const payload = {
          name: labelPath.split("/").pop(),
          color: "#FF0000",
        };

        if (isSubEmailMode) {
          payload.emailId = existingEmailIds;
          payload.subEmailIds = existingSubEmailIds.filter((id) => id !== selectedSubEmailID);
        } else {
          payload.emailId = existingEmailIds.filter((id) => id !== selectedEmailID);
          payload.subEmailIds = existingSubEmailIds;
        }

        await labelService.Update_Label_By_Id(labelObj.id, payload);
      }

      if (TaskId) {
        try {
          const data = await task.UPDATE_TASK(TaskId, { isLabel: true });

        } catch (err) {
          console.error("Error updating task:", err);
        }
      }

      setSelected([]);
      setInitialSelected([]);
      handleClose();
    } catch (error) {
      console.error("Error applying labels:", error);
    } finally {
      setApplying(false);
    }
  }




  return (
    <>
      <CreateLabelDialog
        open={createDialogOpen}
        setOpen={setCreateDialogOpen}
        fetchAllData={fetchLabels}
      />

      <Popover
        open={open && !createDialogOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              mt: 0.5,
              border: "1px solid #e5e7eb",
              borderRadius: "12px",
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
              overflow: "hidden",
            },
          },
        }}
      >
        <Box sx={{ width: 300 }}>

          {/* ── Header ── */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
              py: 1.25,
              borderBottom: "1px solid #f0f0f0",
              backgroundColor: "#f9fafb",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "7px",
                  backgroundColor: "#1a7d3e",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
                  <line x1="7" y1="7" x2="7.01" y2="7" />
                </svg>
              </Box>
              <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#111827" }}>
                Label as
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={handleClose}
              sx={{
                color: "#9ca3af",
                "&:hover": { color: "#374151", backgroundColor: "#f3f4f6" },
                borderRadius: "6px",
                p: "4px",
              }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>

          {/* ── Search ── */}
          <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1.25,
                py: 0.75,
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                backgroundColor: "#fff",
                "&:focus-within": {
                  borderColor: "#1a7d3e",
                  boxShadow: "0 0 0 3px rgba(26,125,62,0.08)",
                },
              }}
            >
              <SearchIcon sx={{ fontSize: 16, color: "#9ca3af", flexShrink: 0 }} />
              <InputBase
                fullWidth
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search labels..."
                inputProps={{ "aria-label": "Search labels" }}
                sx={{ fontSize: "0.82rem", color: "#374151", "& input::placeholder": { color: "#9ca3af" } }}
              />
            </Box>
          </Box>

          {/* ── Label list ── */}
          <List
            dense
            disablePadding
            sx={{
              maxHeight: 240,
              overflowY: "auto",
              px: 2,
              pt: 0.5,
              pb: 1.5,
              "&::-webkit-scrollbar": { width: "4px" },
              "&::-webkit-scrollbar-track": { background: "transparent" },
              "&::-webkit-scrollbar-thumb": { background: "#d1d5db", borderRadius: "4px" },
            }}
          >
            {loading ? (
              <Typography sx={{ py: 3, textAlign: "center", fontSize: "0.82rem", color: "#6b7280" }}>
                Loading…
              </Typography>
            ) : filtered.length > 0 ? (
              filtered.map((labelPath) => {
                const isChecked = selected.includes(labelPath);
                return (
                  <ListItemButton
                    key={labelPath}
                    onClick={() => toggle(labelPath)}
                    dense
                    sx={{
                      py: 0.75,
                      px: 1.5,
                      mb: 0.5,
                      borderRadius: "8px",
                      gap: 1,
                      backgroundColor: isChecked ? "rgba(26,125,62,0.06)" : "transparent",
                      "&:hover": { backgroundColor: isChecked ? "rgba(26,125,62,0.10)" : "#f5f5f5" },
                      transition: "background-color 0.15s ease",
                    }}
                  >
                    <Checkbox
                      tabIndex={-1}
                      disableRipple
                      checked={isChecked}
                      inputProps={{ "aria-label": `Toggle ${labelPath}` }}
                      sx={{
                        p: "4px",
                        flexShrink: 0,
                        color: "#d1d5db",
                        "&.Mui-checked": { color: "#1a7d3e" },
                      }}
                    />
                    <ListItemText
                      primary={labelPath}
                      primaryTypographyProps={{
                        noWrap: true,
                        sx: {
                          fontSize: "0.83rem",
                          fontWeight: isChecked ? 600 : 400,
                          color: isChecked ? "#1a7d3e" : "#374151",
                        },
                      }}
                    />
                  </ListItemButton>
                );
              })
            ) : (
              <Typography sx={{ py: 3, textAlign: "center", fontSize: "0.82rem", color: "#9ca3af" }}>
                No labels found
              </Typography>
            )}
          </List>

          {/* ── Footer ── */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
              py: 1.25,
              borderTop: "1px solid #f0f0f0",
              backgroundColor: "#f9fafb",
            }}
          >
            <Button
              size="small"
              onClick={handleOpenCreateDialog}
              startIcon={
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                </svg>
              }
              sx={{
                fontSize: "0.78rem",
                fontWeight: 600,
                color: "#1a7d3e",
                textTransform: "none",
                px: 1,
                "&:hover": { backgroundColor: "rgba(26,125,62,0.06)" },
              }}
            >
              Create new
            </Button>

            {!isAdd && hasChanges && (
              <Button
                size="small"
                onClick={handleApply}
                disabled={applying}
                sx={{
                  fontSize: "0.78rem",
                  fontWeight: 600,
                  textTransform: "none",
                  backgroundColor: "#1a7d3e",
                  color: "#fff",
                  borderRadius: "7px",
                  px: 2,
                  "&:hover": { backgroundColor: "#166633" },
                  "&.Mui-disabled": { backgroundColor: "#d1d5db", color: "#fff" },
                }}
              >
                {applying ? "Applying…" : "Apply"}
              </Button>
            )}
          </Box>

        </Box>
      </Popover>
    </>
  );
}
